import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Collapse,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import api from '../services/api';

// ── Helpers ────────────────────────────────────────────────────────────────

const fmtUSD = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(v));
};
const fmtInt = (v) => (v == null ? '—' : new Intl.NumberFormat('en-US').format(Number(v)));
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');

function StatusBadge({ status }) {
  if (status === 'in_sync') {
    return <Chip icon={<CheckCircleIcon />} label="In sync" color="success" size="small" />;
  }
  if (status === 'drift') {
    return <Chip icon={<WarningIcon />} label="Drift" color="warning" size="small" />;
  }
  if (status === 'cc_only') {
    return <Chip label="CC only" color="error" size="small" />;
  }
  if (status === 'sage_only') {
    return <Chip label="Sage only" color="error" size="small" />;
  }
  return <Chip label="—" size="small" />;
}

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied!' : label}>
      <IconButton
        size="small"
        onClick={async () => {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        <CopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function QueryPanel({ sageQuery, ccQuery }) {
  const [expanded, setExpanded] = useState(false);
  if (!sageQuery && !ccQuery) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Button
        size="small"
        onClick={() => setExpanded((v) => !v)}
        startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none' }}
      >
        {expanded ? 'Hide queries' : 'Show queries (run these in Sage to verify)'}
      </Button>
      <Collapse in={expanded} unmountOnExit>
        {sageQuery && (
          <Box sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Sage T-SQL
              </Typography>
              <CopyButton text={sageQuery} label="Copy Sage query" />
            </Stack>
            <Box component="pre" sx={{
              background: '#1e293b',
              color: '#e2e8f0',
              p: 1.5,
              borderRadius: 1,
              fontSize: 12,
              fontFamily: 'Menlo, Monaco, "Courier New", monospace',
              overflow: 'auto',
              whiteSpace: 'pre',
              m: 0,
            }}>
              {sageQuery}
            </Box>
          </Box>
        )}
        {ccQuery && (
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Command Center (Postgres)
              </Typography>
              <CopyButton text={ccQuery} label="Copy CC query" />
            </Stack>
            <Box component="pre" sx={{
              background: '#f1f5f9',
              color: '#0f172a',
              p: 1.5,
              borderRadius: 1,
              fontSize: 12,
              fontFamily: 'Menlo, Monaco, "Courier New", monospace',
              overflow: 'auto',
              whiteSpace: 'pre',
              m: 0,
            }}>
              {ccQuery}
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

// ── Single check card ──────────────────────────────────────────────────────

function CheckCard({ title, description, fetcher, renderResult, getReportSnapshot, getOverallStatus }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportStatus, setReportStatus] = useState('in_sync');
  const [reportNotes, setReportNotes] = useState('');
  const [reportSending, setReportSending] = useState(false);
  const [reportToast, setReportToast] = useState(null);

  const run = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetcher();
      setData(res);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  const submitReport = useCallback(async () => {
    setReportSending(true);
    try {
      await api.post('/sage-recon/report', {
        check_name: title,
        status: reportStatus,
        notes: reportNotes || null,
        snapshot: getReportSnapshot ? getReportSnapshot(data) : data,
      });
      setReportOpen(false);
      setReportNotes('');
      setReportToast({ severity: 'success', message: 'Email sent to Tom.' });
    } catch (e) {
      setReportToast({ severity: 'error', message: e.response?.data?.error || e.message });
    } finally {
      setReportSending(false);
    }
  }, [title, reportStatus, reportNotes, data, getReportSnapshot]);

  const overall = data && getOverallStatus ? getOverallStatus(data) : null;

  return (
    <Paper sx={{ p: 2.5, mb: 2 }} elevation={1}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
            {overall && <StatusBadge status={overall} />}
            {data?.checked_at && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                checked {fmtDate(data.checked_at)}
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            startIcon={loading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : (data ? <RefreshIcon /> : <PlayArrowIcon />)}
            onClick={run}
            disabled={loading}
          >
            {data ? 'Re-verify' : 'Verify'}
          </Button>
          {data && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<SendIcon />}
              onClick={() => {
                setReportStatus(overall === 'in_sync' ? 'in_sync' : 'drift');
                setReportOpen(true);
              }}
            >
              Email Tom
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      )}

      {data && !error && (
        <Box sx={{ mt: 2 }}>{renderResult(data)}</Box>
      )}

      {data && (
        <QueryPanel sageQuery={data.sage_query} ccQuery={data.cc_query} />
      )}

      {/* Report dialog */}
      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Email Tom — {title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1}>
              <Chip
                label="✅ In sync"
                color={reportStatus === 'in_sync' ? 'success' : 'default'}
                variant={reportStatus === 'in_sync' ? 'filled' : 'outlined'}
                onClick={() => setReportStatus('in_sync')}
              />
              <Chip
                label="⚠️ Drift"
                color={reportStatus === 'drift' ? 'warning' : 'default'}
                variant={reportStatus === 'drift' ? 'filled' : 'outlined'}
                onClick={() => setReportStatus('drift')}
              />
            </Stack>
            <TextField
              label="Notes for Tom (optional)"
              multiline
              minRows={3}
              value={reportNotes}
              onChange={(e) => setReportNotes(e.target.value)}
              placeholder={reportStatus === 'drift'
                ? "e.g. saw a $54k drift on JTD — Pat suggested it might be the same JCT cost-code double-count..."
                : "e.g. ran the WIP totals — both sides match within $1."}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={reportSending ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SendIcon />}
            onClick={submitReport}
            disabled={reportSending}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!reportToast}
        autoHideDuration={4000}
        onClose={() => setReportToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {reportToast && (
          <Alert severity={reportToast.severity} onClose={() => setReportToast(null)} sx={{ width: '100%' }}>
            {reportToast.message}
          </Alert>
        )}
      </Snackbar>
    </Paper>
  );
}

// ── Tile renderers ─────────────────────────────────────────────────────────

function SideBySideNumber({ label, cc, sage, diff, format = fmtUSD, status }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mt: 0.5 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Command Center</Typography>
          <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{format(cc)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Sage 300 (live)</Typography>
          <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{format(sage)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Difference</Typography>
          <Typography variant="h6" sx={{
            fontFamily: 'monospace',
            color: status === 'in_sync' ? 'success.main' : (status === 'drift' ? 'warning.main' : 'text.primary'),
          }}>
            {diff == null ? '—' : (diff > 0 ? '+' : '') + format(diff)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function SageRecon() {
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    api.get('/sage-recon/last-sync').then((r) => setLastSync(r.data)).catch(() => {});
  }, []);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <CompareArrowsIcon fontSize="large" sx={{ color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Sage Reconciliation</Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Verify Command Center numbers against live Sage 300 data. Each check pulls fresh from Sage
        when you click <strong>Verify</strong>. Expand any tile to see the exact T-SQL Pat would run —
        you can copy/paste it into Sage to confirm independently.
      </Typography>

      {lastSync && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Command Center last synced from Sage on{' '}
          <strong>{fmtDate(lastSync.last_sync)}</strong>
          {' '}— {fmtInt(lastSync.synced_jobs)} jobs.
        </Alert>
      )}

      <CheckCard
        title="Active WIP — Job count, Contract, JTD Cost"
        description="Active jobs (Status ≠ Closed, Remove_from_WIP = 0). The big three numbers Pat reconciles weekly."
        fetcher={() => api.get('/sage-recon/wip-totals').then((r) => r.data)}
        getOverallStatus={(d) => {
          if (d.contract.status === 'drift' || d.jtd.status === 'drift' || d.jobs.status === 'drift') return 'drift';
          if (d.contract.status === 'in_sync' && d.jtd.status === 'in_sync' && d.jobs.status === 'in_sync') return 'in_sync';
          return null;
        }}
        renderResult={(d) => (
          <Box>
            <SideBySideNumber
              label="Active job count"
              cc={d.jobs.cc}
              sage={d.jobs.sage}
              diff={d.jobs.diff}
              format={fmtInt}
              status={d.jobs.status}
            />
            <Divider sx={{ my: 1 }} />
            <SideBySideNumber
              label="Total revised contract"
              cc={d.contract.cc}
              sage={d.contract.sage}
              diff={d.contract.diff}
              status={d.contract.status}
            />
            <Divider sx={{ my: 1 }} />
            <SideBySideNumber
              label="Total JTD cost"
              cc={d.jtd.cc}
              sage={d.jtd.sage}
              diff={d.jtd.diff}
              status={d.jtd.status}
            />
          </Box>
        )}
      />

      <CheckCard
        title="AR — Total Open"
        description="Total open AR across all active jobs. CC sums (sage_billed − sage_paid); Sage uses the vw_TMB_Open_AR_by_Job view."
        fetcher={() => api.get('/sage-recon/ar-total').then((r) => r.data)}
        getOverallStatus={(d) => d.status}
        renderResult={(d) => (
          <SideBySideNumber
            label="Open AR"
            cc={d.cc}
            sage={d.sage}
            diff={d.diff}
            status={d.status}
          />
        )}
      />

      <CheckCard
        title="New Business — Fiscal YTD (Oct 1 → today)"
        description="ACS fiscal year contract + change order totals — Pat's Crystal report rule (JCT_CURRENT__TRANSACTION, Transaction_Type filter)."
        fetcher={() => api.get('/sage-recon/new-business').then((r) => r.data)}
        getOverallStatus={() => null}
        renderResult={(d) => (
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Window: {d.window.from} → {d.window.to}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mt: 1 }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Original Contract</Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{fmtUSD(d.sage.original_contract)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Change Orders</Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{fmtUSD(d.sage.change_orders)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total New Business</Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{fmtUSD(d.sage.total)}</Typography>
              </Box>
            </Box>
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Run Pat's "New Business by Salesperson" Crystal report in Sage with the same date window
              ({d.window.from} → {d.window.to}). Total should match within rounding.
            </Alert>
          </Box>
        )}
      />

      <CheckCard
        title="Per-Job Drift — Active WIP"
        description="Job-by-job comparison of contract & JTD between CC and Sage. Surfaces exactly which jobs are causing any drift you see in the totals above."
        fetcher={() => api.get('/sage-recon/wip-by-job').then((r) => r.data)}
        getOverallStatus={(d) => {
          if ((d.counts.drift || 0) + (d.counts.cc_only || 0) + (d.counts.sage_only || 0) > 0) return 'drift';
          return 'in_sync';
        }}
        getReportSnapshot={(d) => ({ counts: d.counts, first_drifts: (d.rows || []).filter(r => r.status !== 'in_sync').slice(0, 20) })}
        renderResult={(d) => {
          const mismatches = (d.rows || []).filter((r) => r.status !== 'in_sync');
          return (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Chip label={`In sync: ${d.counts.in_sync || 0}`} color="success" size="small" />
                <Chip label={`Drift: ${d.counts.drift || 0}`} color={d.counts.drift ? 'warning' : 'default'} size="small" />
                <Chip label={`CC only: ${d.counts.cc_only || 0}`} color={d.counts.cc_only ? 'error' : 'default'} size="small" />
                <Chip label={`Sage only: ${d.counts.sage_only || 0}`} color={d.counts.sage_only ? 'error' : 'default'} size="small" />
              </Stack>
              {mismatches.length === 0 ? (
                <Alert severity="success">All active WIP jobs match between Command Center and Sage. 🎉</Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Job</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">CC Contract</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Sage Contract</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Δ Contract</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">CC JTD</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Sage JTD</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Δ JTD</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mismatches.map((r) => (
                        <TableRow key={r.job + (r.description || '')} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{r.job}</TableCell>
                          <TableCell sx={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.description}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmtUSD(r.cc_contract)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmtUSD(r.sage_contract)}</TableCell>
                          <TableCell align="right" sx={{
                            fontFamily: 'monospace',
                            color: Math.abs(r.contract_diff) > 1 ? 'warning.main' : 'inherit',
                          }}>
                            {r.contract_diff ? (r.contract_diff > 0 ? '+' : '') + fmtUSD(r.contract_diff) : fmtUSD(0)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmtUSD(r.cc_jtd)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmtUSD(r.sage_jtd)}</TableCell>
                          <TableCell align="right" sx={{
                            fontFamily: 'monospace',
                            color: Math.abs(r.jtd_diff) > 1 ? 'warning.main' : 'inherit',
                          }}>
                            {r.jtd_diff ? (r.jtd_diff > 0 ? '+' : '') + fmtUSD(r.jtd_diff) : fmtUSD(0)}
                          </TableCell>
                          <TableCell><StatusBadge status={r.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          );
        }}
      />
    </Box>
  );
}
